<template>
  <v-container class="container-mobile">
    <v-row class="card">
      <sn-loader
        v-if="loading"
        class="pt-8 pb-8 mt-16 mb-16"
        :loading="true"
        :wait-for-animation="true"
        :speed="1.25"
        :messages="['Please wait...']"
      />
      <template v-else>
        <v-col
          class="action-col"
          :class="classes"
        >
          <hero-text
            :logo="companyLogoUrl"
            :title="splashHeader"
            :subtitle="splashSubheader"
            :website="servicer.website"
          />
          <action-input
            :button-text="homehubLandingPageContinueButton"
            type="email"
            :label-text="splashEmailHint"
            :loading="btnLoading"
            :default-value="inputDefault"
            :rules="emailRules"
            @action-btn-clicked="actionInputClickHandler"
          />
          <div
            v-if="$vuetify.breakpoint.lgAndUp"
            class="
            signin-cta
            sn-subtitle-1 sn-font-weight-regular sn-text-primary-04
          "
          >
            <p>
              {{ splashAlreadyHaveAnAccount }}
              <span
                class="signin-link sn-font-weight-semi-bold"
                data-cy="homehub-landing-page-sign-in"
                :tabindex="0"
                @click="signinLinkClickHandler"
                @keyup.enter="signinLinkClickHandler"
              >
                {{ splashSignIn }}
              </span>
            </p>
          </div>
        </v-col>
        <v-spacer cols="1" />
        <v-col
          v-if="!servicer.borrower_web_content_setting_hide_loan_officer_partner_on_onboarding"
          class="servicer-col d-flex"
          :cols="$vuetify.breakpoint.lgAndUp ? 4 : null"
          justify="end"
          align="center"
        >
          <slot />
        </v-col>
      </template>
    </v-row>
  </v-container>
</template>

<script>
import {
  connect,
  start,
  initialize,
  needExpert
} from '~/components/homehub/Onboarding/Shared/requests.js'
import cookie from 'js-cookie'
import ActionInput from '../Form/ActionTextInput.vue'
import HeroText from './HeroText.vue'
import homehubMixin from '~/components/homehub/Onboarding/Shared/homehubMixin.js'
import snValidationsService from '~/services/snValidationsService.js'
import SnLoader from '~/components/common/snLoader.vue'

export default {
  name: 'LandingHeroBanner',

  components: {
    ActionInput,
    HeroText,
    SnLoader
  },
  mixins: [homehubMixin],
  props: {
    servicer: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      inputDefault: this.$store.userEmail || this.$route.query.email,
      emailRules: [(v) => snValidationsService.email(v) || this.homehubLandingPageEmailErrorMessage],
      sac: this.$route.params.code,
      canTransitionHere: true,
      emailFromShare: this.$route.query.email
    }
  },
  computed: {
    classes () {
      if (this.hideLoanOfficerPartnerOnOnboarding && this.$vuetify.breakpoint.lgAndUp) return 'col col-6 offset-3 text-center'
      else if (this.hideLoanOfficerPartnerOnOnboarding) return 'col text-center'
      else return this.$vuetify.breakpoint.lgAndUp ? 'col col-7' : null
    },
    companyLogoUrl () {
      return this.servicer?.effective_company_logo_url
    },
    homehubLandingPageEmailErrorMessage () {
      return this.$store.borrowerWebContent?.homehubLandingPageEmailErrorMessage || 'Please enter a valid E-mail'
    },
    splashHeader () {
      return this.$store.borrowerWebContent?.splashHeader
    },
    splashSubheader () {
      return this.$store.borrowerWebContent?.splashSubheader
    },
    splashEmailHint () {
      return this.$store.borrowerWebContent?.onboardingYourEmail
    },
    splashAlreadyHaveAnAccount () {
      return this.$store.borrowerWebContent?.alreadyHaveAnAccount
    },
    splashSignIn () {
      return this.$store.borrowerWebContent?.signInLabel
    },
    homehubLandingPageContinueButton () {
      return this.$store.borrowerWebContent?.homehubLandingPageContinueButton
    },
    hideLoanOfficerPartnerOnOnboarding () {
      return this.$store.borrowerWebContent?.hideLoanOfficerPartnerOnOnboarding
    }
  },
  mounted () {
    if (this.$store.response?.data?.current_state) this.$store.response.data.current_state = undefined
    if (this.$route.query.email) this.$store.userEmail = this.$route.query.email
    if (this.$route.query.email_token) {
      this.$store.emailToken = this.$route.query.email_token
      this.$router.replace({ path: this.$route.fullPath, query: { ...this.$route.query, email_token: undefined } }) // remove token from url
    }
  },
  methods: {
    async actionInputClickHandler (inputText) {
      this.btnLoading = true
      this.$store.userEmail = inputText
      const calls = [
        {
          callback: initialize,
          onSuccess: () => {
            this.$store.authGuid = this.$store.response.data.values.auth_guid
            cookie.set('auth_guid', this.$store.response.data.values.auth_guid)
          }
        },
        { callback: () => start(this.$store.authGuid) },
        {
          callback: () => connect({
            activationCode: this.$route.params.code,
            authGuid: this.$store.authGuid,
            userEmail: this.$store.userEmail,
            emailFromShare: this.emailFromShare
          })
        },
        {
          condition: () => this.$store.response.data.current_state === 'new_check_for_expert',
          callback: () => needExpert(this.$store.authGuid)
        }
      ]

      const handleSuccess = () => {
        this.persistState()
        this.performTransition()
      }

      this.chainApiCalls(calls, handleSuccess)
    },
    signinLinkClickHandler () {
      const loginPrefix = 'homehub'

      window.location.href = this.sac
        ? `/${loginPrefix}/login/${this.sac}`
        : `/${loginPrefix}/login`
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~/assets/css/snui";

.card {
  padding: 64px;
  display: flex;
  background-color: $sn-white;
  border-radius: 16px;
}
.action-col h1 {
  margin-top: 35px;
}
.title {
  margin-top: 35px;
  font-size: 48px !important;
}
.subtitle {
  padding-left: 12px;
  margin-top: 16px;
  margin-bottom: 40px;
}
.signin-link {
  cursor: pointer;
  text-decoration: underline;
}

@media screen and (max-width: $medium-screen-size) {
  .container-mobile {
    position: relative;
    border-radius: 16px 16px 0 0;
    background-color: $sn-white;
  }
  .card {
    max-height: inherit;
    flex-direction: column-reverse;
    align-items: center;
    padding: 45px 17px 10px;
  }
  .action-col {
    position: relative;
    margin: 0 -17px;
    padding: 40px 16px;
    border-radius: 8px;
    background-color: $sn-white;
  }
  .servicer-col {
    justify-content: center;
    margin-top: -130px;
  }
}

@media screen and (min-width: ($medium-screen-size + 1)) {
  .card {
    box-shadow: $sn-shadow-depth-3;
  }
  .container-mobile {
    margin-bottom: 16px;
  }
}
</style>
